// Actions a cashier can take on a caja movement from the movements list:
// refund, change the payment method, open its order(s). Payment-backed
// movements go through the payments API (PIN for OrdersCancel when missing);
// manual movements go through the caja-movements API, which never asks for a
// PIN (CajaManage is the only gate, and callers hide the action without it).
import { onScopeDispose, ref, Ref } from 'vue';
import CajaMovementsService from '@/api/services/caja-movements.service';
import PaymentsService from '@/api/services/payments.service';
import { TCajaMovementAction } from '@/domain/caja/caja-movement-row';
import { PaymentMethod } from '@/enums/PaymentMethod';
import { PermissionType } from '@/enums/PermissionType';
import { AlertType } from '@/interfaces/IAlert';
import { IAuthData } from '@/interfaces/IAuthData';
import { ICaja } from '@/interfaces/ICaja';
import { ICajaMovement } from '@/interfaces/ICajaMovement';
import { roundPrice } from '@/utils';
import { i18n } from '@/plugins/i18n';
import { useAuth } from '@/composition/use-auth';
import { useModal } from '@/composition/use-modal';
import { useOrders } from '@/composition/use-orders';
import { usePaymentRefund } from '@/composition/use-payment-refund';
import { usePosMoney } from '@/composition/use-pos-money';
import { useUi } from '@/composition/use-ui';
import { useRestaurantStore } from '@/stores/restaurant.store';
import ModalAdminPin from '@/components/modals/ModalAdminPin.vue';
import ModalChangePaymentMethod from '@/components/modals/pay/ModalChangePaymentMethod.vue';
import PosV2CajaMovementOrdersModal from '@/components/pos-v2/caja/PosV2CajaMovementOrdersModal.vue';

interface IMovementScope {
  restaurantId: number;
  branchId: number;
  cajaId: number;
}

type TRunner = (
  movement: ICajaMovement,
  target: IMovementScope
) => Promise<boolean>;

export function useCajaMovementActions(caja: Ref<ICaja>) {
  const modals = useModal();
  const auth = useAuth();
  const ui = useUi();
  const money = usePosMoney();
  const ordersHelper = useOrders();
  const restaurantStore = useRestaurantStore();
  const { refundPayment } = usePaymentRefund();

  /** Spans dialogs and the request: a second tap while busy is ignored. */
  const busy = ref(false);
  let disposed = false;
  onScopeDispose(() => {
    disposed = true;
  });

  function t(key: string, params?: Record<string, unknown>): string {
    return String(i18n.t(key, params));
  }

  function scope(): IMovementScope | null {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch) return null;
    return {
      restaurantId: restaurant.id,
      branchId: branch.id,
      cajaId: caja.value.id,
    };
  }

  /** Resolves undefined when no PIN is needed, null when dismissed. */
  async function askCancelPin(
    reason: string
  ): Promise<IAuthData | undefined | null> {
    if (auth.hasPermission(PermissionType.OrdersCancel)) return undefined;
    return await modals.showDynamicDialog<IAuthData | null>(
      ModalAdminPin,
      { requiredPermission: PermissionType.OrdersCancel, reason },
      'admin-pin'
    );
  }

  const refund: TRunner = async (movement, target) => {
    const amount = roundPrice(Math.abs(+movement.amount || 0));
    if (movement.paymentId != null) {
      return await refundPayment({
        payment: { id: movement.paymentId, total: amount },
        amount,
        shared: false,
      });
    }

    const confirmed = await modals.confirm({
      title: t('posV2.paymentsRefundTitle'),
      message: t('cajaV2.movementRefundConfirmManual', {
        amount: money.formatComputedMoney(amount),
      }),
    });
    if (!confirmed) return false;

    try {
      await CajaMovementsService.reimburse({
        ...target,
        movementId: movement.id,
      });
      ui.showToast(AlertType.Success, t('posV2.paymentsRefundSuccess'));
      return true;
    } catch (e) {
      ui.serverError(e, t('posV2.paymentsRefundError'));
      return false;
    }
  };

  const changeMethod: TRunner = async (movement, target) => {
    const paymentMethod = await modals.showDynamicDialog<PaymentMethod | null>(
      ModalChangePaymentMethod,
      { currentMethod: movement.paymentMethod ?? null },
      'change-payment-method'
    );
    if (!paymentMethod) return false;

    try {
      if (movement.paymentId != null) {
        const authData = await askCancelPin('payment-change-method');
        if (authData === null) return false;
        await PaymentsService.v2ChangePaymentMethod(
          movement.paymentId,
          paymentMethod,
          authData
        );
      } else {
        await CajaMovementsService.changePaymentMethod({
          ...target,
          movementId: movement.id,
          paymentMethod,
        });
      }
      ui.showToast(AlertType.Success, t('cajaV2.movementMethodChanged'));
      return true;
    } catch (e) {
      ui.serverError(e, t('cajaV2.movementMethodChangeError'));
      return false;
    }
  };

  /** Never changes data: always resolves false. */
  const viewOrders: TRunner = async (movement) => {
    const orderIds = movement.orderIds ?? [];
    if (!orderIds.length) return false;

    try {
      if (orderIds.length === 1) {
        await ordersHelper.openOrder(orderIds[0], false);
      } else {
        // One tenant-scoped batch fetch inside the modal (GET /orders/ids).
        await modals.showDynamicDialog<boolean>(
          PosV2CajaMovementOrdersModal,
          { orderIds },
          'caja-movement-orders'
        );
      }
    } catch (e) {
      ui.serverError(e, t('cajaV2.movementOrdersLoadError'));
    }
    return false;
  };

  const RUNNERS: Record<TCajaMovementAction, TRunner> = {
    refund,
    changeMethod,
    viewOrders,
  };

  /** Resolves true when the movement data changed and must be refetched. */
  async function run(
    action: TCajaMovementAction,
    movement: ICajaMovement
  ): Promise<boolean> {
    if (busy.value) return false;
    // Resolved once, up front: the dialogs take arbitrarily long and the
    // request must hit the caja the movement was listed under.
    const target = scope();
    if (!target) return false;

    busy.value = true;
    try {
      return await RUNNERS[action](movement, target);
    } finally {
      if (!disposed) busy.value = false;
    }
  }

  return { busy, run };
}
